import { Badge, EmptyState, Skeleton } from "../primitives/Feedback.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { toneColor, toneSurface } from "../logic/runState.ts";
import {
  diffChangeLabel,
  diffFileSummary,
  diffLineAriaLabel,
  diffLineMarker,
  diffLineNumberLabel,
  diffTotals,
  diffTransitionLabel,
  type DiffFileView,
  type DiffLineView,
} from "../logic/diff.ts";

export interface RunDiffViewProps {
  readonly files: readonly DiffFileView[];
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly unpublished?: boolean;
  readonly truncated?: boolean;
  readonly title?: string;
}

export function RunDiffView({
  files,
  loading = false,
  error = null,
  unpublished = false,
  truncated = false,
  title = "Changes",
}: RunDiffViewProps) {
  const totals = diffTotals(files);
  const hasTextChanges = files.some((file) => file.content.kind === "text");
  return (
    <section aria-label="Run file changes" style={stackStyle("var(--dayvise-space-md)")}>
      <div style={rowStyle("var(--dayvise-space-sm)")}>
        <h2 style={{ margin: 0, fontFamily: "var(--dayvise-font-family-sans)", fontSize: "var(--dayvise-font-size-lg)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>{title}</h2>
        {totals.files > 0 ? (
          <Badge>
            {`${totals.files} file${totals.files === 1 ? "" : "s"}${hasTextChanges ? ` · +${totals.added} −${totals.removed}` : ""}`}
          </Badge>
        ) : null}
      </div>

      {unpublished ? (
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          These changes are not published to the conversation yet, so they may still change. Downloads return published revisions only.
        </p>
      ) : null}

      {error ? (
        <p role="alert" style={{ ...MUTED_TEXT, margin: 0, color: toneColor("danger") }}>
          {`Changes could not be loaded: ${error}`}
        </p>
      ) : null}

      {loading && files.length === 0 ? (
        <div role="status" aria-label="Loading file changes" style={stackStyle("var(--dayvise-space-sm)")}>
          <Skeleton height="64px" />
          <Skeleton height="64px" />
        </div>
      ) : null}

      {!loading && !error && files.length === 0 ? (
        <EmptyState title="No file changes" description="This run did not record created, edited or deleted files." />
      ) : null}

      {files.map((file) => (
        <DiffFileCard key={`${file.kind}:${file.path}`} file={file} />
      ))}

      {truncated ? (
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          The change list is longer than can be shown here. Open the file directly for the full revision.
        </p>
      ) : null}
    </section>
  );
}

function DiffFileCard({ file }: { readonly file: DiffFileView }) {
  return (
    <Card
      title={file.path}
      subtitle={diffTransitionLabel(file)}
      footer={
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{diffFileSummary(file)}</span>
      }
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
          <Badge tone={file.kind === "created" ? "success" : file.kind === "deleted" ? "danger" : "accent"}>{diffChangeLabel(file.kind)}</Badge>
          {file.content.kind === "binary" ? <Badge tone="warning">Binary</Badge> : null}
        </div>

        {file.content.kind === "binary" ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>
            Binary file. Compare by size and hash; no line diff is shown because none exists.
          </p>
        ) : file.content.lines.length === 0 ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>No line differences: the recorded revisions have identical text.</p>
        ) : (
          <div role="group" aria-label={`Line differences for ${file.path}`} style={{ margin: 0, fontFamily: "var(--dayvise-font-family-mono)", fontSize: "var(--dayvise-font-size-sm)", overflowX: "auto" }}>
            {file.content.lines.map((line, index) => (
              <DiffLineRow key={`${index}:${line.kind}`} line={line} />
            ))}
          </div>
        )}

        {file.content.kind === "text" && file.content.truncated ? (
          <p style={{ ...MUTED_TEXT, margin: 0 }}>Only part of this file is shown. The full revisions are still available for download.</p>
        ) : null}
      </div>
    </Card>
  );
}

function DiffLineRow({ line }: { readonly line: DiffLineView }) {
  const tone = line.kind === "added" ? "success" : line.kind === "removed" ? "danger" : "neutral";
  return (
    <div
      aria-label={diffLineAriaLabel(line)}
      style={{
        display: "grid",
        gridTemplateColumns: "4ch 8ch 2ch 1fr",
        gap: "var(--dayvise-space-xxs)",
        whiteSpace: "pre",
        background: toneSurface(tone),
        color: line.kind === "context" ? "var(--dayvise-color-text-muted)" : "var(--dayvise-color-text)",
      }}
    >
      <span aria-hidden="true" style={{ textAlign: "right", opacity: 0.7 }}>{diffLineNumberLabel(line).split("|")[0]}</span>
      <span aria-hidden="true" style={{ textAlign: "right", opacity: 0.7 }}>{diffLineNumberLabel(line).split("|")[1]}</span>
      <span aria-hidden="true" style={{ textAlign: "center", fontWeight: "var(--dayvise-font-weight-semibold)" }}>{diffLineMarker(line.kind)}</span>
      <span>{line.missingFinalNewline ? `${line.text} (no final newline)` : line.text}</span>
    </div>
  );
}
